import React, { useMemo, useContext, createContext, useCallback } from 'react';
import { visitorClient } from 'clients';
import { HTTPError } from 'clients/HTTPClient';
import useReducerWithMiddleware from '../useReducerWithMiddleware';
import unauthorized from '../unauthorized';
import {
    KIOSK_VISITORS_REQUESTED,
    KIOSK_VISITORS_SUCCESS,
    KIOSK_VISITORS_FAILED,
    ADD_KIOSK_VISITOR_REQUESTED,
    ADD_KIOSK_VISITOR_SUCCESS,
    ADD_KIOSK_VISITOR_FAILED,
    VALIDATE_KIOSK_VISITOR_REQUESTED,
    VALIDATE_KIOSK_VISITOR_SUCCESS,
    VALIDATE_KIOSK_VISITOR_FAILED,
} from './constants';
import visitorKioskReducer, { initialVisitorKioskState, VisitorKioskState } from './reducer';
import { VisitorValidationSource } from 'utils/constants';

type VisitorForm = {
    plate: string;
    state: string;
    country: string;
};

type ValidationRequest = {
    pk: number;
    minutes: number;
    formattedDate?: string;
};

interface VisitorKioskActions {
    getKioskVisitors: (spotId: number) => void;
    addKioskVisitor: (spotId: number, visitor: VisitorForm) => void;
    validateKioskVisitor: (spotId: number, validation: ValidationRequest) => void;
}

interface VisitorKioskContextType extends VisitorKioskState, VisitorKioskActions {}

export const VisitorKioskContext = createContext<VisitorKioskContextType | null>(null);

export const VisitorKioskProvider: React.FunctionComponent<React.PropsWithChildren<object>> = ({ children }) => {
    const [state, dispatch] = useReducerWithMiddleware(
        visitorKioskReducer,
        { ...initialVisitorKioskState },
        [],
        [unauthorized]
    );

    const getKioskVisitors = useCallback(
        async (spotPk: number) => {
            dispatch({ type: KIOSK_VISITORS_REQUESTED });

            try {
                const visitors = await visitorClient.getVisitorsForKiosk(spotPk);
                dispatch({ type: KIOSK_VISITORS_SUCCESS, payload: { visitors } });
            } catch (error) {
                dispatch({
                    type: KIOSK_VISITORS_FAILED,
                    payload: { error, message: error?.toString() || 'Fetching visitors failed.' },
                });
            }
        },
        // until this is fixed: https://github.com/reactjs/react.dev/issues/1889,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const addKioskVisitor = useCallback(
        async (spotPk: number, { plate, state, country }: VisitorForm) => {
            dispatch({ type: ADD_KIOSK_VISITOR_REQUESTED });
            try {
                const newVisitor = await visitorClient.addVisitor({
                    spot: spotPk,
                    plate,
                    state,
                    country,
                    validation_source: VisitorValidationSource.KIOSK,
                });
                const newVisitorCar =
                    newVisitor.car_details.plate +
                    (newVisitor.car_details.state ? ` - ${newVisitor.car_details.state}` : '');
                const visitors = await visitorClient.getVisitorsForKiosk(spotPk);

                dispatch({
                    type: ADD_KIOSK_VISITOR_SUCCESS,
                    payload: {
                        message: 'Added visitor with plate: ' + newVisitorCar,
                        visitors,
                    },
                });

                getKioskVisitors(spotPk);
            } catch (error) {
                let errorMessage;
                if (HTTPError.isHTTPError(error)) {
                    errorMessage = (error as HTTPError<{ detail: string }>).cause?.detail || error.message;
                } else {
                    errorMessage = error?.toString() || 'Could not add visitor';
                }
                dispatch({
                    type: ADD_KIOSK_VISITOR_FAILED,
                    payload: { message: errorMessage },
                });
            }
        },
        // until this is fixed: https://github.com/reactjs/react.dev/issues/1889,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const validateKioskVisitor = useCallback(
        async (spotPk: number, { pk, minutes, formattedDate }: ValidationRequest) => {
            dispatch({ type: VALIDATE_KIOSK_VISITOR_REQUESTED });
            try {
                const { plate } = await visitorClient.validateKioskVisitor({
                    visitor_pk: pk,
                    minutes,
                    validation_source: VisitorValidationSource.KIOSK,
                });
                const validUntil = formattedDate ? `until ${formattedDate}` : `for ${minutes} minutes`;
                const visitors = await visitorClient.getVisitorsForKiosk(spotPk);

                dispatch({
                    type: VALIDATE_KIOSK_VISITOR_SUCCESS,
                    payload: {
                        message: `Validated visitor with plate ${plate} ${validUntil}`,
                        visitors,
                    },
                });
            } catch (error) {
                let errorMessage;
                if (HTTPError.isHTTPError(error)) {
                    errorMessage = (error as HTTPError<{ detail: string }>).cause?.detail || error.message;
                } else {
                    errorMessage = error?.toString() || 'Could not edit visitor';
                }
                dispatch({
                    type: VALIDATE_KIOSK_VISITOR_FAILED,
                    payload: { message: errorMessage },
                });
            }
        },
        // until this is fixed: https://github.com/reactjs/react.dev/issues/1889,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const value = useMemo<VisitorKioskContextType>(() => {
        return {
            ...state,
            getKioskVisitors,
            addKioskVisitor,
            validateKioskVisitor,
        };
    }, [state, getKioskVisitors, addKioskVisitor, validateKioskVisitor]);

    return <VisitorKioskContext.Provider value={value}>{children}</VisitorKioskContext.Provider>;
};

export const useVisitorKiosk = () => {
    const context = useContext(VisitorKioskContext);
    if (!context) {
        throw new Error('Error: useVisitorKiosk should be wrapped by VisitorKioskProvider.');
    }
    return context;
};
